import { useEffect, useRef, useState } from "react";
import { animate, useReducedMotion } from "framer-motion";
import { EASE } from "./nl";

/* Live metric transition (Motion Contract §6): when a refreshed normalized
   value changes, interpolate once with restrained semantic emphasis, then
   settle. Never pulses continuously. Reduced motion / hidden document →
   instant swap with color-only emphasis. */

export const AnimatedMetric = ({ value, format, className, baseColor = "#ffffff", testid }: {
  value: number | null | undefined;
  format: (n: number | null | undefined) => string;
  className?: string;
  baseColor?: string;
  testid?: string;
}) => {
  const reduced = useReducedMotion();
  const [display, setDisplay] = useState<number | null | undefined>(value);
  const [flash, setFlash] = useState<string | null>(null);
  const prev = useRef(value);

  useEffect(() => {
    const from = prev.current;
    prev.current = value;
    if (value == null || from == null || from === value) {
      setDisplay(value);
      return;
    }
    setFlash(value > from ? "#20c77a" : "#f0445e");
    const settle = setTimeout(() => setFlash(null), 900);
    if (reduced || document.visibilityState === "hidden") {
      setDisplay(value);
      return () => clearTimeout(settle);
    }
    const controls = animate(from, value, { duration: 0.5, ease: EASE, onUpdate: (v) => setDisplay(v) });
    return () => { controls.stop(); clearTimeout(settle); };
  }, [value, reduced]);

  return (
    <span className={className} data-testid={testid}
      style={{ color: flash || baseColor, transition: "color 0.45s ease" }}>
      {/* Intermediate tween digits are visual-only; AT reads the settled value. */}
      <span aria-hidden="true">{format(display)}</span>
      <span className="sr-only">{format(value)}</span>
    </span>
  );
};
